/**
 * Permission Integrity Service - Enterprise Security Layer
 * 
 * Provides cryptographic signing and tamper detection for permission states
 * to ensure client-side permission validation cannot be compromised.
 * 
 * Security Features:
 * - SHA-256 cryptographic checksums
 * - Tamper detection via integrity verification
 * - Automatic rollback on security violations
 * - Security event emission for monitoring
 */

import { systemLogger } from '@/lib/logging';

// Security event types for monitoring
export type SecurityEventType = 
  | 'INTEGRITY_VIOLATION'
  | 'TAMPER_DETECTED'
  | 'PERMISSION_BYPASS_ATTEMPT'
  | 'CACHE_POISONING_DETECTED'
  | 'SUSPICIOUS_ACTIVITY';

export interface SecurityEvent {
  type: SecurityEventType;
  timestamp: string;
  userId?: string;
  details: Record<string, unknown>;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  source: string;
  // CSP violation properties
  blockedURI?: string;
  violatedDirective?: string;
  effectiveDirective?: string;
  // Additional metadata
  metadata?: Record<string, unknown>;
}

export interface PermissionState {
  userId: string;
  permissions: Record<string, boolean>;
  timestamp: number;
  signature: string;
  checksum: string;
  userContext?: {
    id: string;
    email?: string;
    company_id?: string;
    is_system_admin?: boolean;
    app_role?: 'USER' | 'COMPANY_ADMIN' | 'ADMIN';
  };
}

export interface IntegrityResult {
  isValid: boolean;
  violations: string[];
  shouldRollback: boolean;
  securityEvents: SecurityEvent[];
}

class PermissionIntegrityService {
  private eventListeners: Set<(_event: SecurityEvent) => void> = new Set();
  private violationThreshold = 3; // Max violations before auto-logout
  private violationCount = 0;

  constructor() {
    // Ensure security key exists in sessionStorage on construction
    this.getOrGenerateSecurityKey();
  }

  /**
   * Get existing security key or generate new one and persist it
   */
  private getOrGenerateSecurityKey(): string {
    const sessionKey = sessionStorage.getItem('permission_security_key');
    if (sessionKey) {
      return sessionKey;
    }
    
    const newKey = this.generateSecurityKey();
    sessionStorage.setItem('permission_security_key', newKey);
    return newKey;
  }

  /**
   * Generate a cryptographically secure key for this session
   */
  private generateSecurityKey(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Sign permission state with cryptographic signature
   */
  async signPermissionState(
    userId: string, 
    permissions: Record<string, boolean>
  ): Promise<PermissionState> {
    const timestamp = Date.now();
    const dataToSign = JSON.stringify({ userId, permissions, timestamp });
    
    // Get fresh security key from sessionStorage to ensure synchronization
    const currentSecurityKey = this.getOrGenerateSecurityKey();
    
    // Create signature using Web Crypto API
    const encoder = new TextEncoder();
    const data = encoder.encode(dataToSign + currentSecurityKey);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const signature = Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');

    // Create integrity checksum
    const checksumData = encoder.encode(JSON.stringify({ 
      userId, 
      permissions, 
      timestamp, 
      signature 
    }));
    const checksumBuffer = await crypto.subtle.digest('SHA-256', checksumData);
    const checksum = Array.from(new Uint8Array(checksumBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');

    return {
      userId,
      permissions,
      timestamp,
      signature,
      checksum
    };
  }

  /**
   * Verify integrity of permission state
   */
  async verifyIntegrity(permissionState: PermissionState): Promise<IntegrityResult> {
    const violations: string[] = [];
    const securityEvents: SecurityEvent[] = [];

    try {
      // Verify signature
      const expectedSignature = await this.generateSignature(
        permissionState.userId,
        permissionState.permissions,
        permissionState.timestamp
      );

      if (expectedSignature !== permissionState.signature) {
        
        violations.push('Invalid signature detected');
        securityEvents.push(this.createSecurityEvent(
          'TAMPER_DETECTED',
          'CRITICAL',
          'permission-integrity',
          {
            userId: permissionState.userId,
            expectedSignature: expectedSignature.substring(0, 8) + '...',
            actualSignature: permissionState.signature.substring(0, 8) + '...',
            reason: 'Signature mismatch'
          }
        ));
      }

      // Verify checksum
      const expectedChecksum = await this.generateChecksum(permissionState);
      if (expectedChecksum !== permissionState.checksum) {
        violations.push('Checksum verification failed');
        securityEvents.push(this.createSecurityEvent(
          'INTEGRITY_VIOLATION',
          'CRITICAL',
          'permission-integrity',
          {
            userId: permissionState.userId,
            reason: 'Checksum mismatch indicates data tampering'
          }
        ));
      }

      // Check timestamp validity (not too old, not from future)
      const now = Date.now();
      const maxAge = 5 * 60 * 1000; // 5 minutes max age
      const timeDiff = now - permissionState.timestamp;

      if (timeDiff > maxAge) {
        violations.push('Permission state expired');
        securityEvents.push(this.createSecurityEvent(
          'SUSPICIOUS_ACTIVITY',
          'MEDIUM',
          'permission-integrity',
          {
            userId: permissionState.userId,
            age: timeDiff,
            maxAge,
            reason: 'Stale permission state detected'
          }
        ));
      }

      if (timeDiff < -30000) { // 30 seconds tolerance for clock skew
        violations.push('Permission state from future');
        securityEvents.push(this.createSecurityEvent(
          'TAMPER_DETECTED',
          'HIGH',
          'permission-integrity',
          {
            userId: permissionState.userId,
            timeDiff,
            reason: 'Future timestamp indicates tampering'
          }
        ));
      }

      // Emit security events
      securityEvents.forEach(event => this.emitSecurityEvent(event));

      // Update violation count
      if (violations.length > 0) {
        this.violationCount += violations.length;
      }

      return {
        isValid: violations.length === 0,
        violations,
        shouldRollback: violations.length > 0 && this.violationCount >= this.violationThreshold,
        securityEvents
      };

    } catch (_error) {
      const criticalEvent = this.createSecurityEvent(
        'INTEGRITY_VIOLATION',
        'CRITICAL',
        'permission-integrity',
        {
          error: _error instanceof Error ? _error.message : 'Unknown error',
          userId: permissionState.userId
        }
      );

      this.emitSecurityEvent(criticalEvent);

      return {
        isValid: false,
        violations: [`Integrity verification failed: ${_error}`],
        shouldRollback: true,
        securityEvents: [criticalEvent]
      };
    }
  }

  /**
   * Generate signature for permission data
   */
  private async generateSignature(
    userId: string,
    permissions: Record<string, boolean>,
    timestamp: number
  ): Promise<string> {
    const dataToSign = JSON.stringify({ userId, permissions, timestamp });
    
    // Get fresh security key from sessionStorage to ensure synchronization
    const currentSecurityKey = this.getOrGenerateSecurityKey();
    
    const encoder = new TextEncoder();
    const data = encoder.encode(dataToSign + currentSecurityKey);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Generate checksum for complete permission state
   */
  private async generateChecksum(permissionState: Omit<PermissionState, 'checksum'>): Promise<string> {
    const encoder = new TextEncoder();
    const checksumData = encoder.encode(JSON.stringify(permissionState));
    const checksumBuffer = await crypto.subtle.digest('SHA-256', checksumData);
    
    return Array.from(new Uint8Array(checksumBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Create a security event
   */
  private createSecurityEvent(
    type: SecurityEventType,
    severity: SecurityEvent['severity'],
    source: string,
    details: Record<string, unknown>
  ): SecurityEvent {
    return {
      type,
      timestamp: new Date().toISOString(),
      severity,
      source,
      details
    };
  }

  /**
   * Emit security event to all listeners
   */
  private emitSecurityEvent(event: SecurityEvent): void {
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (_error) {
        systemLogger.error('Failed operation:', _error);
      }
    });
  }

  /**
   * Add security event listener
   */
  onSecurityEvent(listener: (_event: SecurityEvent) => void): () => void {
    this.eventListeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.eventListeners.delete(listener);
    };
  }

  /**
   * Clear violation count (used after successful re-authentication)
   */
  clearViolations(): void {
    this.violationCount = 0;
  }

  /**
   * Get current violation count
   */
  getViolationCount(): number {
    return this.violationCount;
  }

  /**
   * Perform emergency rollback of permission state
   */
  async performEmergencyRollback(userId: string): Promise<void> {
    const rollbackEvent = this.createSecurityEvent(
      'INTEGRITY_VIOLATION',
      'CRITICAL',
      'permission-integrity',
      {
        userId,
        action: 'emergency_rollback',
        violationCount: this.violationCount,
        reason: 'Too many integrity violations detected'
      }
    );

    this.emitSecurityEvent(rollbackEvent);

    // Clear any potentially compromised cached data
    try {
      localStorage.removeItem(`permission_cache_${userId}`);
      sessionStorage.removeItem(`permission_state_${userId}`);
      sessionStorage.removeItem('permission_security_key'); // Clear security key to force regeneration
    } catch (_error) {
      systemLogger.warn('Warning:', _error);
    }

    // Force user to re-authenticate by triggering logout
    window.dispatchEvent(new CustomEvent('permission-security-violation', {
      detail: { userId, reason: 'Integrity violation threshold exceeded' }
    }));
  }

  /**
   * Validate DOM element hasn't been tampered with
   */
  validateDOMIntegrity(element: Element, expectedAttributes: Record<string, string>): boolean {
    try {
      for (const [attr, expectedValue] of Object.entries(expectedAttributes)) {
        const actualValue = element.getAttribute(attr);
        if (actualValue !== expectedValue) {
          this.emitSecurityEvent(this.createSecurityEvent(
            'TAMPER_DETECTED',
            'HIGH',
            'dom-integrity',
            {
              element: element.tagName,
              attribute: attr,
              expected: expectedValue,
              actual: actualValue
            }
          ));
          return false;
        }
      }
      return true;
    } catch (_error) {
      this.emitSecurityEvent(this.createSecurityEvent(
        'INTEGRITY_VIOLATION',
        'MEDIUM',
        'dom-integrity',
        { error: _error instanceof Error ? _error.message : 'Unknown error' }
      ));
      return false;
    }
  }

  /**
   * Clear persisted security key (for logout or security reset)
   */
  clearSecurityKey(): void {
    try {
      sessionStorage.removeItem('permission_security_key');
      // Next call to getOrGenerateSecurityKey() will create a new key
    } catch (_error) {
      systemLogger.warn('Warning:', _error);
    }
  }
}

// Export singleton instance
export const permissionIntegrity = new PermissionIntegrityService();

// Types are already exported above where they are defined